import { useMemo, useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';

import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Link, Stack, Typography } from '@mui/material';

import { paths } from 'src/routes/paths';

import PriceLabel from 'src/components/products/price-label';
import { DataGridNoRowsOverlay } from 'src/components/datagrid';
import { QuantityInput } from 'src/components/products/quantity-input';
import SurplusProductLabel from 'src/components/products/surplus-product-label';
import PromotionalProductLabel from 'src/components/products/promotional-product-label';

import { ActionsCell, ActiveOperations } from './actions-cell';
import { EnhancedFavouritesItemModel } from './use-favourite-products-list';

interface ProductTableProps {
  items: EnhancedFavouritesItemModel[];
  handleQuantityChange: (productStockId: string, delta: number) => void;
  handleUpdate: (cartItemId: string, quantity: number, productId: string) => void;
  handleAddToCart: (productStockId: string, quantity: number, productId: string) => void;
  handleRemoveFromCart: (cartItemId: string, productId: string) => void;
  deleteItem: (itemId: string) => Promise<void>;
  activeOperations: ActiveOperations;
}

export default function FavouriteProductsListTable({
  items,
  handleQuantityChange,
  handleUpdate,
  handleAddToCart,
  handleRemoveFromCart,
  deleteItem,
  activeOperations,
}: ProductTableProps) {
  const navigate = useNavigate();

  const navigateToProductDetails = useCallback(
    (productStockId: string) => {
      navigate({ to: paths.dashboard.products.productDetails(productStockId) });
    },
    [navigate]
  );

  const navigateToProducerDetails = useCallback(
    (producerId: string) => {
      navigate({ to: paths.dashboard.producer.producerDetails(producerId) });
    },
    [navigate]
  );

  const baseColumnDef: Partial<GridColDef<EnhancedFavouritesItemModel>> = useMemo(
    () => ({
      flex: 1,
      headerClassName: 'product-table-header',
      filterable: false,
    }),
    []
  );

  const columns: GridColDef<EnhancedFavouritesItemModel>[] = useMemo(
    () => [
      {
        ...baseColumnDef,
        field: 'productName',
        headerName: 'Product',
        minWidth: 220,
        renderCell: (params) => (
          <Stack ml={1} alignItems="center" direction="row" justifyContent="center" gap={2}>
            {/* Display surplus / promotion icon where relevant */}
            {params.row.surplusEntry?.typeOfPrice === 'surplus' && <SurplusProductLabel />}
            {params.row.surplusEntry?.typeOfPrice === 'promotion' && <PromotionalProductLabel />}
            <Link
              component="button"
              onClick={() => navigateToProductDetails(params.row.productStockId)}
            >
              <Typography variant="body2" color="textPrimary" align="left">
                {params.row.name}
              </Typography>
              <Typography variant="body2" color="GrayText" align="left">
                {params.row.units}
              </Typography>
            </Link>
          </Stack>
        ),
      },
      {
        ...baseColumnDef,
        field: 'producerName',
        headerName: 'Producer',
        minWidth: 120,
        maxWidth: 190,
        valueGetter: (params) => params.row.producerName || params.row.brand || '---',
        renderCell: (params) => {
          if (params.row.producerId) {
            return (
              <Link
                component="button"
                onClick={() => navigateToProducerDetails(params.row.producerId!)}
              >
                <Typography variant="body2" color="textPrimary" align="left">
                  {params.row.producerName}
                </Typography>
              </Link>
            );
          }
          return <Typography variant="body2">{params.row.brand || '---'}</Typography>;
        },
      },
      {
        ...baseColumnDef,
        field: 'price',
        headerName: 'Price',
        maxWidth: 120,
        minWidth: 120,
        valueGetter: (params) => params.row.price,
        renderCell: (params: any) => (
          <PriceLabel
            surplusEntry={params.row.surplusEntry}
            price={params.row.originalPrice}
            isEstimate={params.row.weightType === 'Guide Weight'}
          />
        ),
      },
      {
        ...baseColumnDef,
        field: 'quantity',
        headerName: 'Quantity',
        sortable: false,
        renderCell: (params) => (
          <QuantityInput
            productName={params.row.name}
            value={params.row.quantity}
            onChange={(value) =>
              handleQuantityChange(params.row.productStockId, value - params.row.quantity)
            }
            minimumValue={1}
            maximumValue={Number(params.row.surplusEntry?.reservedQuantity)}
          />
        ),
        maxWidth: 180,
        minWidth: 180,
      },
      {
        ...baseColumnDef,
        field: 'totalPrice',
        headerName: 'Total Price',
        maxWidth: 120,
        minWidth: 120,
        valueGetter: (params) => params.row.totalPrice,
        valueFormatter: (params) => `£${params.value.toFixed(2)}`,
      },
      {
        field: 'actions',
        ...baseColumnDef,
        headerName: '',
        sortable: false,
        filterable: false,
        align: 'center',
        headerAlign: 'center',
        minWidth: 220,
        maxWidth: 220,
        disableClickEventBubbling: true,
        renderCell: (params) => (
          <ActionsCell
            product={params.row}
            handleUpdate={handleUpdate}
            handleAddToCart={handleAddToCart}
            handleRemoveFromCart={handleRemoveFromCart}
            deleteItem={deleteItem}
            activeOperations={activeOperations}
          />
        ),
      },
    ],
    [
      activeOperations,
      baseColumnDef,
      deleteItem,
      handleAddToCart,
      handleQuantityChange,
      handleRemoveFromCart,
      handleUpdate,
      navigateToProductDetails,
      navigateToProducerDetails,
    ]
  );

  return (
    <DataGrid
      loading={false} // TODO
      sx={{
        width: '100%',
        visibility: items === undefined ? 'hidden' : 'visible',
        '.MuiDataGrid-overlayWrapper': { height: '100px' },
        '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': { py: '4px' },
        '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': { py: '8px' },
        '&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell': { py: '12px' },
      }}
      slots={{
        noRowsOverlay: () => <DataGridNoRowsOverlay overlayText="No products to display" />,
        noResultsOverlay: () => <DataGridNoRowsOverlay overlayText="No products to display" />,
      }}
      sortingMode="client" // TODO: Server side sorting
      rows={items || []}
      columns={columns}
      hideFooterSelectedRowCount
      disableRowSelectionOnClick
      disableColumnMenu
      hideFooterPagination
      pageSizeOptions={[]} // Hide the page size options
      getRowHeight={() => 'auto'}
      autoHeight
      scrollbarSize={0} // There is no fixed height thus no scrollbar allowence needed
      getEstimatedRowHeight={() => 65}
    />
  );
}
